import { useCallback, useEffect, useState } from "react";
import { Customer, CustomerType } from "@/api/customers";
import {
  CustomerUser,
  isValidEmail,
  TmsCustomerUser,
  TmsUserWithReason,
} from "@/api/users";
import {
  Checkbox,
  CheckboxVisibility,
  DetailsList,
  MessageBar,
  MessageBarType,
  Persona,
  PersonaSize,
  PrimaryButton,
  SearchBox,
  SelectionMode,
  Spinner,
  SpinnerSize,
  Toggle,
} from "@fluentui/react";
import { DataGridColumn } from "@/ui-controls/DataGrid";

interface Props {
  customer: Customer;
  //customerUsers: CustomerUser[];
  tmsCustomerUsers: TmsCustomerUser[];
  onClose?: () => any;
  onSave?: (
    tmsCustomerUsers: TmsCustomerUser[],
    useTmsPassword: boolean
  ) => Promise<any>;
}

const nonSelectableColumns: DataGridColumn[] = [
  {
    key: "reason",
    name: "Reason",
    fieldName: "reason",
    minWidth: 200,
    maxWidth: 400,
    isRowHeader: true,
    isResizable: true,
    isPadded: true,
  },
  {
    key: "emailAddress",
    name: "UserId NextGen Dispatch",
    fieldName: "EmailAddress",
    minWidth: 250,
    maxWidth: 450,
    isRowHeader: true,
    isResizable: true,
    isPadded: true,
  },
  {
    key: "tmsUsername",
    name: "TMS Username",
    fieldName: "TmsUsername",
    minWidth: 150,
    maxWidth: 250,
    isRowHeader: true,
    isResizable: true,
    isPadded: true,
  },
];

const MigrateTmsCustomerUsersForm = (props: Props) => {
  const [selectableTmsCustomerUsers, setSelectableTmsCustomerUsers] = useState<
    TmsCustomerUser[]
  >([]);
  const [
    selectableFilteredTmsCustomerUsers,
    setSelectableFilteredTmsCustomerUsers,
  ] = useState<TmsCustomerUser[]>([]);

  const [
    nonSelectableFilteredTmsCustomerUsers,
    setNonSelectableFilteredTmsCustomerUsers,
  ] = useState<TmsCustomerUser[]>([]);

  const [showSelectableTmsUsers, setShowSelectableTmsUsers] =
    useState<boolean>(true);
  const isShipper =
    props.customer?.CustomerTypeId === CustomerType.Shipper ||
    props.customer?.CustomerType?.toLowerCase() === "shipper";
  const [useTmsPassword, setUseTmsPassword] = useState<boolean>(true);

  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const allChecked =
    selectableFilteredTmsCustomerUsers.length > 0 &&
    selectableFilteredTmsCustomerUsers.every((i) =>
      selectedKeys.has(i.EmailAddress)
    );
  const toggleAll = (__: any, checked?: boolean) => {
    if (checked) {
      setSelectedKeys(
        new Set(selectableFilteredTmsCustomerUsers.map((i) => i.EmailAddress))
      );
    } else {
      setSelectedKeys(new Set());
    }
  };
  const toggleOne = useCallback((key: string, checked?: boolean) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);

  const selectableColumns: DataGridColumn[] = [
    {
      key: "select",
      name: "",
      fieldName: "select",
      minWidth: 32,
      maxWidth: 32,
      isResizable: false,
      // render the “select all” checkbox in the header cell
      onRenderHeader: () => (
        <Checkbox
          checked={allChecked}
          disabled={isSaving}
          onChange={toggleAll}
          styles={{ root: { margin: 0 } }}
        />
      ),
      onRender: (item: TmsCustomerUser) => (
        <div className="centered-row">
          <Checkbox
            checked={selectedKeys.has(item.EmailAddress)}
            disabled={isSaving}
            onChange={(_, checked) => toggleOne(item.EmailAddress, checked)}
            styles={{ root: { margin: 0 } }}
          />
        </div>
      ),
    },
    {
      key: "persona",
      name: "",
      fieldName: "",
      minWidth: 50,
      maxWidth: 70,
      isResizable: false,
      onRender: (item: CustomerUser) => {
        // Derive display name from the email (before the '@')
        const emailPrefix = item.EmailAddress?.split("@")[0] || "";
        return (
          <div className="centered-row">
            <Persona
              hidePersonaDetails
              text={emailPrefix}
              size={PersonaSize.size32}
            />
          </div>
        );
      },
    },
    {
      key: "emailAddress",
      name: "UserId NextGen Dispatch",
      fieldName: "EmailAddress",
      minWidth: 250,
      maxWidth: 450,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
      onRender: (item: TmsCustomerUser) => (
        <div className="centered-row">
          <span>{item.EmailAddress}</span>
        </div>
      ),
    },
    {
      key: "tmsUsername",
      name: "Tms Username",
      fieldName: "TmsUsername",
      minWidth: 150,
      maxWidth: 250,
      isRowHeader: true,
      isResizable: true,
      isPadded: true,
      onRender: (item: TmsCustomerUser) => (
        <div className="centered-row">
          <span>{item.TmsUsername}</span>
        </div>
      ),
    },
  ];

  useEffect(() => {
    const all = props.tmsCustomerUsers;

    // 1) Missing TmsUsername
    const invalidUsername: TmsUserWithReason[] = all
      .filter((u) => !u.TmsUsername)
      .map((u) => ({ ...u, reason: "Missing TmsUsername" }));

    // 2) Missing or malformed EmailAddress
    const invalidEmail: TmsUserWithReason[] = all
      .filter((u) => !u.EmailAddress || !isValidEmail(u.EmailAddress))
      .map((u) => ({ ...u, reason: "Invalid EmailAddress" }));

    // 3) Duplicated EmailAddress (case-insensitive)
    const emailCounts = all.reduce<Record<string, number>>((acc, u) => {
      const e = u.EmailAddress?.toLowerCase();
      if (e) acc[e] = (acc[e] || 0) + 1;
      return acc;
    }, {});
    const duplicatedEmail: TmsUserWithReason[] = all
      .filter((u) => {
        const e = u.EmailAddress?.toLowerCase();
        return e != null && emailCounts[e] > 1;
      })
      .map((u) => ({ ...u, reason: "Duplicated EmailAddress" }));

    // 4) Duplicated TmsUsername (case-insensitive)
    const usernameCounts = all.reduce<Record<string, number>>((acc, u) => {
      const t = u.TmsUsername?.toLowerCase();
      if (t) acc[t] = (acc[t] || 0) + 1;
      return acc;
    }, {});
    const duplicatedUsername: TmsUserWithReason[] = all
      .filter((u) => {
        const t = u.TmsUsername?.toLowerCase();
        return t != null && usernameCounts[t] > 1;
      })
      .map((u) => ({ ...u, reason: "Duplicated TmsUsername" }));

    // 5) Build a Set of invalid keys, using lowercased composite
    const invalidKeySet = new Set<string>();
    [
      ...invalidUsername,
      ...invalidEmail,
      ...duplicatedEmail,
      ...duplicatedUsername,
    ].forEach((u) => {
      const key = `${u.EmailAddress?.toLowerCase() || ""}|${
        u.TmsUsername?.toLowerCase() || ""
      }`;
      invalidKeySet.add(key);
    });

    // 6a) Valid users
    const validUsers: TmsCustomerUser[] = all.filter((u) => {
      const key = `${u.EmailAddress?.toLowerCase() || ""}|${
        u.TmsUsername?.toLowerCase() || ""
      }`;
      return !invalidKeySet.has(key);
    });

    // 6b) Combine invalids but keep first reason per lowercased key
    const combinedInvalids = [
      ...invalidUsername,
      ...invalidEmail,
      ...duplicatedEmail,
      ...duplicatedUsername,
    ];
    const seen = new Set<string>();
    const invalidWithReason: TmsUserWithReason[] = [];
    combinedInvalids.forEach((u) => {
      const key = `${u.EmailAddress?.toLowerCase() || ""}|${
        u.TmsUsername?.toLowerCase() || ""
      }`;
      if (!seen.has(key)) {
        seen.add(key);
        invalidWithReason.push(u);
      }
    });

    // Sort case-insensitively by email
    const sortedInvalid = invalidWithReason.sort((a, b) =>
      (a.EmailAddress ?? "")
        .toLowerCase()
        .localeCompare((b.EmailAddress ?? "").toLowerCase())
    );

    setNonSelectableFilteredTmsCustomerUsers(sortedInvalid);
    setSelectableTmsCustomerUsers(validUsers);
    setSelectableFilteredTmsCustomerUsers(validUsers);
  }, [props.tmsCustomerUsers]);

  useEffect(() => {
    if (!isShipper) {
      setUseTmsPassword(false);
    }
  }, [isShipper]);

  // Auto-clear success message after 5 seconds
  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => {
        setSuccessMessage(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  // Auto-clear error message after 8 seconds
  useEffect(() => {
    if (errorMessage) {
      const timer = setTimeout(() => {
        setErrorMessage(null);
      }, 8000);
      return () => clearTimeout(timer);
    }
  }, [errorMessage]);

  const handleUserSearch = (searchValue: string) => {
    const filteredUsers = selectableTmsCustomerUsers.filter(
      (user) =>
        user.EmailAddress.toLowerCase().includes(searchValue.toLowerCase()) ||
        user.TmsUsername?.toLowerCase().includes(searchValue.toLowerCase())
    );
    setSelectableFilteredTmsCustomerUsers(filteredUsers);
  };

  const handleSave = async () => {
    const selectedUsers = selectableFilteredTmsCustomerUsers.filter((u) =>
      selectedKeys.has(u.EmailAddress)
    );

    if (selectedUsers.length === 0) return;

    setIsSaving(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      await props.onSave?.(selectedUsers, useTmsPassword);
      setSuccessMessage(
        `Successfully migrated ${selectedUsers.length} user(s).`
      );

      // Close modal after a brief delay to show success message
      setTimeout(() => {
        props.onClose?.();
      }, 2000);
    } catch (error: any) {
      console.error("Error migrating users:", error);

      if (error?.errors) {
        const fieldErrors = Object.values(error.errors) as string[][];
        if (fieldErrors.length && fieldErrors[0].length) {
          setErrorMessage(fieldErrors[0][0]);
          return;
        }
      }

      setErrorMessage("Failed to migrate users. Please try again.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      {/* full-screen overlay when saving */}
      {isSaving && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100vw",
            height: "100vh",
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 9999, // Very high z-index to cover everything
          }}
        >
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              backgroundColor: "white",
              padding: "24px",
              borderRadius: "8px",
              boxShadow: "0 4px 16px rgba(0, 0, 0, 0.3)",
            }}
          >
            <Spinner size={SpinnerSize.large} label="Creating users..." />
          </div>
        </div>
      )}
      <div
        className="page-header-container"
        style={{
          padding: "10px 10px 10px 0px",
          position: "sticky",
          top: "50px",
          zIndex: 100,
          background: "#fff",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            paddingTop: 5,
            gap: 12,
          }}
        >
          <Toggle
            checked={showSelectableTmsUsers}
            onText="Selectable TMS Users"
            offText="Non Selectable TMS Users"
            disabled={isSaving}
            onChange={() => setShowSelectableTmsUsers(!showSelectableTmsUsers)}
          />
          {isShipper && showSelectableTmsUsers && (
            <Toggle
              checked={useTmsPassword}
              onText="Keep user password"
              offText="Keep user password"
              disabled={isSaving}
              onChange={(_, checked) => setUseTmsPassword(!!checked)}
            />
          )}
        </div>

        {showSelectableTmsUsers && (
          <div style={{ display: "flex", alignItems: "center" }}>
            <SearchBox
              placeholder="Search"
              onSearch={handleUserSearch}
              disabled={isSaving}
            />
            <PrimaryButton
              text={isSaving ? "Creating..." : "Create Users"}
              style={{ marginLeft: 5 }}
              disabled={selectedKeys.size === 0 || isSaving}
              onClick={handleSave}
            />
          </div>
        )}
      </div>
      {showSelectableTmsUsers && (
        <DetailsList
          items={selectableFilteredTmsCustomerUsers}
          compact={false}
          columns={selectableColumns}
          isHeaderVisible={true}
          selectionMode={SelectionMode.none}
          checkboxVisibility={CheckboxVisibility.hidden}
        />
      )}
      {!showSelectableTmsUsers && (
        <DetailsList
          items={nonSelectableFilteredTmsCustomerUsers}
          compact={false}
          columns={nonSelectableColumns}
          isHeaderVisible={true}
          selectionMode={SelectionMode.none}
          checkboxVisibility={CheckboxVisibility.hidden}
        />
      )}

      {/* Success message */}
      {successMessage && (
        <MessageBar
          messageBarType={MessageBarType.success}
          isMultiline={false}
          onDismiss={() => setSuccessMessage(null)}
          styles={{
            root: {
              position: "fixed",
              bottom: 0,
              left: 0,
              width: "100%",
              zIndex: 10000,
            },
          }}
        >
          {successMessage}
        </MessageBar>
      )}

      {/* Error message */}
      {errorMessage && (
        <MessageBar
          messageBarType={MessageBarType.error}
          isMultiline={false}
          onDismiss={() => setErrorMessage(null)}
          styles={{
            root: {
              position: "fixed",
              bottom: successMessage ? 56 : 0,
              left: 0,
              width: "100%",
              zIndex: 10000,
            },
          }}
        >
          {errorMessage}
        </MessageBar>
      )}
    </>
  );
};

export default MigrateTmsCustomerUsersForm;
